import { useState, useCallback, useRef, useEffect } from 'react'

interface DragState {
  isDragging: boolean
  dragIndex: number | null
  dragOffset: { x: number; y: number }
}

interface ResizeState {
  isResizing: boolean
  resizeColumn: string | null
  startX: number
  startWidth: number
}

export const useColumnDragResize = (
  onColumnReorder?: (fromIndex: number, toIndex: number) => void,
  onColumnResize?: (columnKey: string, width: number) => void
) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragIndex: null,
    dragOffset: { x: 0, y: 0 },
  })

  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    resizeColumn: null,
    startX: 0,
    startWidth: 0,
  })

  const dragPreviewRef = useRef<HTMLDivElement>(null)

  // Handle column drag start
  const handleDragStart = useCallback(
    (index: number, event: React.MouseEvent) => {
      if (!onColumnReorder) return

      event.preventDefault()
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()

      setDragState({
        isDragging: true,
        dragIndex: index,
        dragOffset: {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        },
      })
    },
    [onColumnReorder]
  )

  // Handle column resize start
  const handleResizeStart = useCallback(
    (columnKey: string, event: React.MouseEvent) => {
      if (!onColumnResize) {
        console.warn('No onColumnResize handler provided')
        return
      }

      event.preventDefault()
      event.stopPropagation()

      console.log('Starting resize for column:', columnKey)

      // Find the table header element
      const resizeHandle = event.currentTarget as HTMLElement
      const headerElement = resizeHandle.closest('th') as HTMLElement

      if (!headerElement) {
        console.error('Could not find header element')
        return
      }

      // Get the current actual width of the column
      const currentWidth = headerElement.getBoundingClientRect().width
      console.log('Current width:', currentWidth)

      setResizeState({
        isResizing: true,
        resizeColumn: columnKey,
        startX: event.clientX,
        startWidth: currentWidth,
      })

      // Add visual feedback
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [onColumnResize]
  )

  // Handle mouse move for dragging and resizing
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (dragState.isDragging && dragPreviewRef.current) {
        // Update drag preview position
        dragPreviewRef.current.style.left = `${event.clientX - dragState.dragOffset.x}px`
        dragPreviewRef.current.style.top = `${event.clientY - dragState.dragOffset.y}px`
      }

      if (
        resizeState.isResizing &&
        resizeState.resizeColumn &&
        onColumnResize
      ) {
        // Calculate new width
        const deltaX = event.clientX - resizeState.startX
        const newWidth = Math.max(50, resizeState.startWidth + deltaX)
        console.log('Resizing:', resizeState.resizeColumn, 'to', newWidth)
        onColumnResize(resizeState.resizeColumn, newWidth)
      }
    }

    const handleMouseUp = (event: MouseEvent) => {
      if (
        dragState.isDragging &&
        dragState.dragIndex !== null &&
        onColumnReorder
      ) {
        // Find drop target
        const dropTarget = document.elementFromPoint(
          event.clientX,
          event.clientY
        )
        const headerRow = dropTarget?.closest('tr')
        const headers = headerRow?.querySelectorAll('th')

        if (headers) {
          const dropIndex = Array.from(headers).findIndex(
            (th) => th.contains(dropTarget as any) || th === dropTarget
          )

          // Adjust for checkbox column
          const adjustedDropIndex = dropIndex > 0 ? dropIndex - 1 : dropIndex

          if (
            adjustedDropIndex !== -1 &&
            adjustedDropIndex !== dragState.dragIndex
          ) {
            onColumnReorder(dragState.dragIndex, adjustedDropIndex)
          }
        }
      }

      // Reset states
      setDragState({
        isDragging: false,
        dragIndex: null,
        dragOffset: { x: 0, y: 0 },
      })
      setResizeState({
        isResizing: false,
        resizeColumn: null,
        startX: 0,
        startWidth: 0,
      })

      // Reset cursor
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    if (dragState.isDragging || resizeState.isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [dragState, resizeState, onColumnReorder, onColumnResize])

  return {
    dragState,
    resizeState,
    dragPreviewRef,
    handleDragStart,
    handleResizeStart,
  }
}
